/**
 * Animal utility functions
 * Provides reusable functions for animal data processing
 */

/**
 * HealthScore threshold (0-100) below which an animal is considered suspicious.
 * Note: Some backend components historically used other scales/thresholds; the web app standard is 0-100.
 */
export const SUSPICIOUS_HEALTH_SCORE_THRESHOLD = 30;
const HEALTH_STATUSES = new Set([
	"healthy",
	"attention",
	"warning",
	"critical",
	"emergency",
]);
const HEALTH_SEVERITIES = new Set(["none", "low", "medium", "high", "critical"]);

/**
 * Normalizes an animal's HealthScore into a finite number (0-100 scale) when possible.
 * @param {Object} animal - Animal object
 * @returns {number|null} HealthScore as number, or null if unavailable/invalid
 */
export function getAnimalHealthScore(animal) {
	if (!animal) return null;
	if (animal.SchemaVersion === 2 && animal.EvaluationQuality !== 'ready') return null;
	const value = animal.HealthScore;
	if (typeof value === "number") {
		return Number.isFinite(value) ? value : null;
	}
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) return null;
		const num = Number(trimmed);
		return Number.isFinite(num) ? num : null;
	}
	return null;
}

function normalizeHealthEnum(value, allowedValues) {
	if (typeof value !== "string") return null;
	const normalized = value.trim().toLowerCase();
	return allowedValues.has(normalized) ? normalized : null;
}

export function inferHealthStatusFromScore(score) {
	if (typeof score !== "number" || !Number.isFinite(score)) return null;
	if (score >= 80) return "healthy";
	if (score >= 60) return "attention";
	if (score >= 40) return "warning";
	if (score >= 20) return "critical";
	return "emergency";
}

/**
 * Resolves the customer-facing state from the canonical Watchdog evidence.
 * Stale/missing data never falls back to a healthy state.
 */
export function getAnimalHealthState(animal) {
	if (!animal) return "unknown";
	if (animal.DataStale === true) return "stale";
	if (animal.SchemaVersion === 2 && animal.EvaluationQuality !== 'ready') return 'unknown';

	const status = normalizeHealthEnum(animal.HealthStatus, HEALTH_STATUSES);
	const severity = normalizeHealthEnum(animal.HealthSeverity, HEALTH_SEVERITIES);

	if (severity === "critical") return "critical";
	if (
		severity === "high" &&
		(!status || status === "healthy" || status === "attention")
	) {
		return "warning";
	}
	if (
		(severity === "medium" || severity === "low") &&
		status === "healthy"
	) {
		return "attention";
	}
	if (status) return status;
	if (severity === "high") return "warning";
	if (severity === "medium" || severity === "low") return "attention";

	const score = getAnimalHealthScore(animal);
	if (score !== null) return inferHealthStatusFromScore(score);
	if (animal.Sus || animal.Suspicious) return "warning";
	return "unknown";
}

export function requiresAnimalHealthReview(
	animal,
	threshold = SUSPICIOUS_HEALTH_SCORE_THRESHOLD
) {
	if (!animal || animal.DataStale === true || (animal.SchemaVersion === 2 && animal.EvaluationQuality !== 'ready')) return false;

	const severity = normalizeHealthEnum(animal.HealthSeverity, HEALTH_SEVERITIES);
	if (severity && severity !== "none") return true;

	const status = normalizeHealthEnum(animal.HealthStatus, HEALTH_STATUSES);
	if (status === "warning" || status === "critical" || status === "emergency") {
		return true;
	}

	const score = getAnimalHealthScore(animal);
	if (score !== null) return score < threshold;
	return Boolean(animal.Sus || animal.Suspicious);
}

/**
 * Checks if an animal is suspicious based on HealthScore (preferred) with a legacy flag fallback.
 * @param {Object} animal - Animal object
 * @param {number} [threshold] - Suspicious threshold for HealthScore
 * @returns {boolean} True if suspicious
 */
export function isAnimalSuspicious(
	animal,
	threshold = SUSPICIOUS_HEALTH_SCORE_THRESHOLD
) {
	return requiresAnimalHealthReview(animal, threshold);
}

/**
 * Sets/normalizes the in-memory suspicious flags (Sus/Suspicious) to match the HealthScore-based rule.
 * This does not persist changes to Firestore; it only normalizes the objects used by the UI.
 * @param {Object} animal - Animal object (mutated)
 * @param {number} [threshold] - Suspicious threshold for HealthScore
 * @returns {Object} The same animal object
 */
export function normalizeAnimalSuspiciousFlags(
	animal,
	threshold = SUSPICIOUS_HEALTH_SCORE_THRESHOLD
) {
	if (!animal) return animal;
	const suspicious = isAnimalSuspicious(animal, threshold);
	animal.Sus = suspicious;
	animal.Suspicious = suspicious;
	return animal;
}

/**
 * Checks if an animal is active
 * Handles multiple active state formats: "Sending", true, "true"
 * @param {Object} animal - Animal object
 * @returns {boolean} True if animal is active
 */
export function isAnimalActive(animal) {
	if (!animal || animal.Active === undefined || animal.Active === null) {
		return false;
	}
	
	// Handle normalized "Sending" state
	if (animal.Active === "Sending") {
		return true;
	}
	
	// Handle boolean true
	if (animal.Active === true) {
		return true;
	}
	
	// Handle string "true"
	if (animal.Active === "true") {
		return true;
	}
	
	// Handle falsy values
	if (animal.Active === false || animal.Active === "false" || animal.Active === "Inactive" || animal.Active === "0") {
		return false;
	}
	
	// Default: any truthy value is considered active (defensive)
	return !!animal.Active;
}

/**
 * Deduplicates animals by MAC address, keeping the most recent active one
 * Prioritizes active animals over inactive ones, then prefers most recent by timestamp
 * 
 * @param {Array} animals - Array of animal objects
 * @returns {Array} Deduplicated array of animals
 */
export function deduplicateAnimalsByMAC(animals) {
	if (!Array.isArray(animals) || animals.length === 0) {
		return [];
	}

	const macMap = new Map();
	
	animals.forEach((animal) => {
		if (!animal.MAC) return;
		
		const existing = macMap.get(animal.MAC);
		if (!existing) {
			macMap.set(animal.MAC, animal);
		} else {
			// Prefer active animals, then most recent
			const existingIsActive = isAnimalActive(existing);
			const currentIsActive = isAnimalActive(animal);
			
			if (currentIsActive && !existingIsActive) {
				macMap.set(animal.MAC, animal);
			} else if (currentIsActive === existingIsActive) {
				// Both same active status, prefer most recent
				const existingTime = existing.t || existing.UpdatedAt || 0;
				const currentTime = animal.t || animal.UpdatedAt || 0;
				if (currentTime > existingTime) {
					macMap.set(animal.MAC, animal);
				}
			}
		}
	});
	
	return Array.from(macMap.values());
}

/**
 * Filters animals to only include active ones
 *
 * @param {Array} animals - Array of animal objects
 * @returns {Array} Array of active animals
 */
export function filterActiveAnimals(animals) {
	if (!Array.isArray(animals)) {
		return [];
	}
	return animals.filter((animal) => isAnimalActive(animal));
}

/**
 * Selects the animals a farmer should see counted anywhere in the app: every
 * animal on the herd list that has not been archived.
 *
 * This is deliberately NOT `isAnimalActive` — "active" describes the sensor
 * state (is the tag sending?), while "visible" describes the herd roster. The
 * header metric bar and the dashboard tiles used to answer "how many animals do
 * I have?" with two different numbers because one filtered `Archived` and the
 * other did not.
 *
 * @param {Array} animals - Array of animal objects (null/undefined tolerated)
 * @returns {Array} Array of non-archived animals
 */
export function selectVisibleAnimals(animals) {
	if (!Array.isArray(animals)) {
		return [];
	}
	return animals.filter((animal) => animal && !animal.Archived);
}

/**
 * Count companion to {@link selectVisibleAnimals} — the single rule every
 * animal-count surface must use.
 *
 * @param {Array} animals - Array of animal objects (null/undefined tolerated)
 * @returns {number} Number of non-archived animals
 */
export function selectVisibleAnimalCount(animals) {
	return selectVisibleAnimals(animals).length;
}

